import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import toast from 'react-hot-toast';

/**
 * "Rate your recent orders" - shown on both the buyer and seller dashboards.
 *
 * Ratings only exist because someone bothers to leave one, so this sits on the
 * page they already visit rather than behind a menu item, and asks for one
 * order at a time instead of presenting a queue.
 */
interface Pending {
  orderId: string;
  reference: string;
  productTitle: string;
  productImage?: string;
  yourRole: 'BUYER' | 'SELLER';
  rating: { id: string; name: string; handle?: string };
}

function Stars({ value, onChange, size = 'lg' }: { value: number; onChange: (n: number) => void; size?: 'lg' | 'sm' }) {
  const [hover, setHover] = useState(0);
  const shown = hover || value;
  return (
    <div className="flex items-center gap-1" onMouseLeave={() => setHover(0)}>
      {[1, 2, 3, 4, 5].map((n) => (
        <button
          key={n}
          type="button"
          aria-label={`${n} star${n > 1 ? 's' : ''}`}
          onMouseEnter={() => setHover(n)}
          onClick={() => onChange(n)}
          className={`${size === 'lg' ? 'text-[30px]' : 'text-[18px]'} leading-none transition ${
            n <= shown ? 'text-gold' : 'text-warmline'
          } hover:scale-110`}
        >
          ★
        </button>
      ))}
    </div>
  );
}

const STAR_WORDS = ['', 'Poor', 'Not great', 'OK', 'Good', 'Excellent'];

export function RatingPrompt() {
  const qc = useQueryClient();
  const [open, setOpen] = useState<Pending | null>(null);
  const [stars, setStars] = useState(0);
  const [accuracy, setAccuracy] = useState(0);
  const [communication, setCommunication] = useState(0);
  const [dispatch, setDispatch] = useState(0);
  const [comment, setComment] = useState('');

  const { data: pending = [] } = useQuery<Pending[]>({
    queryKey: ['reviews-pending'],
    queryFn: async () => (await api.get('/reviews/pending')).data,
  });

  const submit = useMutation({
    mutationFn: async () =>
      (await api.post('/reviews', {
        orderId: open!.orderId,
        stars,
        comment: comment.trim() || undefined,
        // Buyers judge the item and the seller; sellers judge the buyer.
        accuracyStars: open!.yourRole === 'BUYER' ? accuracy || undefined : undefined,
        dispatchStars: open!.yourRole === 'BUYER' ? dispatch || undefined : undefined,
        communicationStars: communication || undefined,
      })).data,
    onSuccess: (res: any) => {
      toast.success(res?.message ?? 'Thanks for the rating.');
      close();
      qc.invalidateQueries({ queryKey: ['reviews-pending'] });
    },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Could not save your rating.'),
  });

  function close() {
    setOpen(null);
    setStars(0); setAccuracy(0); setCommunication(0); setDispatch(0); setComment('');
  }

  if (!pending.length) return null;
  const next = pending[0];
  const isBuyer = next.yourRole === 'BUYER';

  return (
    <>
      <div className="card-flat p-5 border-gold/30 bg-gold/5">
        <div className="flex items-start gap-4">
          <div className="w-14 h-14 rounded-luxe bg-warmline overflow-hidden shrink-0">
            {next.productImage && <img src={next.productImage} alt="" className="w-full h-full object-cover" />}
          </div>
          <div className="flex-1 min-w-0">
            <div className="stamp text-[9px] text-gold-dark">
              {pending.length > 1 ? `${pending.length} ORDERS TO RATE` : 'RATE YOUR ORDER'}
            </div>
            <p className="text-[14px] mt-1 leading-snug">
              How was {isBuyer ? 'buying' : 'selling'} <span className="font-medium">{next.productTitle}</span>
              {' '}{isBuyer ? 'from' : 'to'} {next.rating.name?.split(' ')[0]}?
            </p>
            <p className="text-[12px] text-mute mt-1">
              Ratings stay hidden until you both rate each other, so nobody can react to their score.
            </p>
          </div>
          <button onClick={() => setOpen(next)} className="btn btn-gold btn-sm shrink-0">Rate</button>
        </div>
      </div>

      {open && (
        <div className="fixed inset-0 z-50 bg-ink/60 backdrop-blur-sm grid place-items-center p-4" onClick={close}>
          <div className="bg-white rounded-2xl w-full max-w-[440px] overflow-hidden overflow-y-auto max-h-[90vh]" onClick={(e) => e.stopPropagation()}>
            <div className="px-6 py-5 border-b border-warmline">
              <div className="stamp text-gold-dark text-[9px]">
                {isBuyer ? 'RATE THE SELLER' : 'RATE THE BUYER'}
              </div>
              <h3 className="display text-2xl mt-1">{open.rating.name?.split(' ')[0]}</h3>
              <p className="text-[12px] text-mute mt-1 truncate">
                {open.productTitle} · <span className="mono">{open.reference}</span>
              </p>
            </div>

            <div className="p-6 space-y-5">
              <div>
                <label className="stamp text-[10px] text-mute block mb-2">OVERALL</label>
                <div className="flex items-center gap-3">
                  <Stars value={stars} onChange={setStars} />
                  {stars > 0 && <span className="text-[13px] text-mute">{STAR_WORDS[stars]}</span>}
                </div>
              </div>

              {/* Sub-scores differ by side: a buyer can judge whether the item
                  matched its description; a seller cannot. */}
              {isBuyer ? (
                <>
                  <div className="flex items-center justify-between">
                    <span className="text-[13px]">Item as described</span>
                    <Stars value={accuracy} onChange={setAccuracy} size="sm" />
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-[13px]">Dispatch speed</span>
                    <Stars value={dispatch} onChange={setDispatch} size="sm" />
                  </div>
                </>
              ) : (
                <div className="flex items-center justify-between">
                  <span className="text-[13px]">Paid promptly</span>
                  <Stars value={dispatch} onChange={setDispatch} size="sm" />
                </div>
              )}

              <div className="flex items-center justify-between">
                <span className="text-[13px]">Communication</span>
                <Stars value={communication} onChange={setCommunication} size="sm" />
              </div>

              <div>
                <label className="stamp text-[10px] text-mute block mb-1.5">COMMENT (OPTIONAL)</label>
                <textarea
                  className="input w-full h-20 resize-none"
                  maxLength={1000}
                  placeholder={isBuyer
                    ? 'Was it as described? Well packed? Quick to arrive?'
                    : 'Smooth payment? Easy to deal with?'}
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                />
              </div>

              {stars > 0 && stars <= 2 && (
                <p className="text-[12px] text-mute leading-relaxed bg-warmline/40 rounded-luxe p-3">
                  Sorry it went badly. If something is actually wrong with the order - wrong item, damage,
                  never arrived - report it from the order itself so Souqli can step in. A low rating alone
                  doesn’t open a case.
                </p>
              )}
            </div>

            <div className="px-6 pb-6 flex gap-2">
              <button onClick={close} className="btn btn-outline flex-1">Not now</button>
              <button
                disabled={stars === 0 || submit.isPending}
                onClick={() => submit.mutate()}
                className="btn btn-primary flex-1"
              >
                {submit.isPending ? 'Sending…' : 'Submit rating'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
